import {
    Alignment, Color, DebouncedFunction, DOMUtil, DynamicLoader, getThemeColor, LogManager, StringUtil, StyleUtil
} from "@mcleod/core";
import { AnchorMutation, AnchorProps } from "../Anchor";
import { Component, Panel } from "../..";

export interface AnchoredComponentPositionerProps {
    anchorProps: AnchorProps;
    anchoredComponent: Component;
    showPointer?: boolean;
}

export enum AnchoredComponentType {
    TOOLTIP = "tooltip",
    OVERLAY = "overlay"
}

const positionerClassPath = "components/page/positioner/floating-ui/FloatingUIPositioner";

const log = LogManager.getLogger("components.page.positioner.AnchoredComponentPositioner");
const POINTER_SIZE = 7;

/**
 * Abstract base class for positioning anchored components (e.g., tooltips, overlays) relative to an anchor element.
 * Subclasses must implement `listenForPositionUpdates` and `computePosition` to define the positioning logic.
 */
export abstract class AnchoredComponentPositioner implements AnchoredComponentPositionerProps {
    componentType: AnchoredComponentType;
    anchorProps: AnchorProps;
    anchoredComponent: Component;
    pointer: Panel;
    pointerPseudoElement: HTMLStyleElement;
    showPointer: boolean;
    protected currentMutation: AnchorMutation;
    private debouncedOnMutate: (elem: HTMLElement, mutation: AnchorMutation) => void;

    protected constructor(props: AnchoredComponentPositionerProps, componentType: AnchoredComponentType) {
        log.debug("Creating positioner for the", componentType, "component anchored to", props.anchorProps.anchor);

        Object.assign(this, props);
        this.componentType = componentType;

        if (typeof this.anchorProps.onMutate === "function") {
            this.debouncedOnMutate = DebouncedFunction.create((elem: HTMLElement, mutation: AnchorMutation) => {
                this.anchorProps.onMutate(elem, mutation)
            }, 100);
        }

        if (StringUtil.isEmptyString(this.anchoredComponent.style.width))
            this.anchoredComponent.style.width = "max-content";
        this.anchoredComponent.style.position = "absolute";

        this.anchorProps.position ??= this.getDefaultPosition();
        this.currentMutation = { newRect: this.componentElement.getBoundingClientRect(), newAnchor: {...this.anchorProps} };
        log.debug("Initial anchor position:", this.anchorProps.position, "for anchor element:", this.anchorElement);

        if (this.showPointer === true) {
            this.addPointer();
        }
    }

    abstract listenForPositionUpdates(): void;
    abstract computePosition(): Promise<AnchorMutation>;

    get isTooltipType(): boolean {
        return this.componentType === AnchoredComponentType.TOOLTIP;
    }

    get isOverlayType(): boolean {
        return this.componentType === AnchoredComponentType.OVERLAY;
    }

    protected get pointerSize(): number {
        return this.pointer != null ? POINTER_SIZE : 0;
    }

    protected get anchorElement() : HTMLElement{
        const anchor = this.anchorProps.anchor;
        if (anchor instanceof Component) {
            if (this.isTooltipType && anchor.tooltipAnchor != null)
                return anchor.tooltipAnchor;
            return anchor._element;
        }
        return anchor;
    }

    get componentElement() : HTMLElement{
        return this.anchoredComponent._element;
    }

    private addPointer() {
        this.pointer = new Panel({ borderWidth: this.pointerSize, padding: 0, rowBreak: false, backgroundColor: "transparent" });
        this.pointer.style.position = "absolute";
        this.pointer.style.border = "none";
        this.createPointerStylePseudoElement();
    }

    private createPointerStylePseudoElement() {
        this.pointerPseudoElement = document.createElement('style');
        this.pointerPseudoElement.id = `${this.anchoredComponent.id}-pointer`;
        this.anchoredComponent.addUnmountListener(() => {
            document.head.removeChild(this.pointerPseudoElement);
        });
        document.head.appendChild(this.pointerPseudoElement);
    }

    async computeAndUpdatePosition() {
        log.debug("Computing position for component anchored to", this.anchorElement, "with initial position:", this.anchorProps.position);
        this.currentMutation = await this.computePosition();
        log.debug("Computed new position:", this.currentMutation.newAnchor.position, "at coordinates:", { x: this.currentMutation.newRect.x, y: this.currentMutation.newRect.y });
        this.updatePosition();
    }

    private updatePosition() {
        requestAnimationFrame(() => {
            const {x, y} = this.currentMutation.newRect;
            log.debug("Updating component position to x:", x, "y:", y);

            this.anchoredComponent.setStyleProps({
                left: `${x}px`,
                top: `${y}px`
            });
            this.adjustPointer();
            this.debouncedOnMutate?.(this.componentElement, this.currentMutation);
        });
    }

    private adjustPointer(): void {
        if (this.pointer == null) {
            return;
        }

        const previouslySetStyles: Partial<CSSStyleDeclaration> = this.pointer["__adjustedStyles"] || {};
        for (const key in previouslySetStyles) {
            this.pointer.style[key] = "";
        }
        this.pointerPseudoElement.textContent = "";

        const style = this.getAdjustedPointerStyle();
        this.pointer.setStyleProps(style);
        this.pointer["__adjustedStyles"] = style;
        this.adjustPointerPseudoElements(style);
    }

    private getCommonTriangleCSS(): string {
        return `
            background-color: transparent;    
            border-style: solid;
            color: transparent;
            content: '';
            height: 0;
            position: absolute;
            width: 0;
        `;
    }
    private getBorderTriangleCSS(style: Partial<CSSStyleDeclaration>): string {
        const anchoredComponentId = this.anchoredComponent.id;
        const position = this.currentMutation.newAnchor.position;
        const pointerSize = this.pointerSize;
        const borderColor = getThemeColor(this.anchoredComponent.borderColor);
        const borderWidth = this.anchoredComponent.borderWidth;
        let result = `
            #${anchoredComponentId}::before {
                ${this.getCommonTriangleCSS()}
                border-width: ${pointerSize + borderWidth}px;
                z-index: 1;
        `;
        switch (position) {
            case Alignment.RIGHT:
                result += `
                    right: 100%;
                    /* top: 50%; */
                    top: ${style.top};
                    transform: translateY(-50%);
                    border-color: transparent ${borderColor} transparent transparent;
                `;
                break;
            case Alignment.LEFT:
                result += `
                    left: 100%;
                    /* top: 50%; */
                    top: ${style.top};
                    transform: translateY(-50%);
                    border-color: transparent transparent transparent ${borderColor};
                `;
                break;
            case Alignment.BOTTOM:
                result += `
                    bottom: 100%;
                    /* left: 50%; */
                    left: ${style.left};
                    transform: translateX(-50%);
                    border-color: transparent transparent ${borderColor} transparent;
                `;
                break;
            case Alignment.TOP:
            default:
                result += `
                    top: 100%;
                    /* left: 50%; */
                    left: ${style.left};
                    transform: translateX(-50%);
                    border-color: ${borderColor} transparent transparent transparent;
                `;
                break;
        }
        result += '}';
        return result;
    }

    private getInnerTriangleCSS(style: Partial<CSSStyleDeclaration>): string {
        const anchoredComponentId = this.anchoredComponent.id;
        const position = this.currentMutation.newAnchor.position;
        const pointerSize = this.pointerSize;
        const pointerColor = this.determinePointerColor();
        let result = `
            #${anchoredComponentId}::after {
                ${this.getCommonTriangleCSS()}
                border-width: ${pointerSize}px;
                z-index: 2;
        `;
        
        switch (position) {
            case Alignment.RIGHT:
                result += `
                    right: 100%;
                    top: ${style.top};
                    transform: translateY(-50%);
                    border-color: transparent ${pointerColor} transparent transparent;
                `;
                break;
                
            case Alignment.LEFT:
                result += `
                    left: 100%;
                    top: ${style.top};
                    transform: translateY(-50%);
                    border-color: transparent transparent transparent ${pointerColor};
                `;
                break;
                
            case Alignment.BOTTOM:
                result += `
                    bottom: 100%;
                    left: ${style.left};
                    transform: translateX(-50%);
                    border-color: transparent transparent ${pointerColor} transparent;
                `;
                break;
                
            case Alignment.TOP:
            default:
                result += `
                    top: 100%;
                    left: ${style.left};
                    transform: translateX(-50%);
                    border-color: ${pointerColor} transparent transparent transparent;
                `;
                break;
        }
        result += '}';
        return result;
    }

    private adjustPointerPseudoElements(style: Partial<CSSStyleDeclaration>) {
        this.ensurePointerInsideBorder(style);
        const styleTagCSS = `${this.getBorderTriangleCSS(style)} ${this.getInnerTriangleCSS(style)}`;
        this.pointerPseudoElement.textContent = styleTagCSS;
    }

    /**
     * Adjusts the pointer CSS to keep the pointer arrow from extending past the tooltip's right/bottom edge
     * @param style The CSS style object that contains the positioning information for the pointer
     */
    private ensurePointerInsideBorder(style: Partial<CSSStyleDeclaration>) {
        const tooltipHeightWidth = DOMUtil.getElementHeightWidth(this.anchoredComponent._element, {
            includeBorder: false,
            includeMargin: false,
            includePadding: true
        });
        const pointerWidth = DOMUtil.convertStyleAttrToNumber(style.width);
        const position = this.currentMutation.newAnchor.position;
        switch (position) {
            case Alignment.TOP:
            case Alignment.BOTTOM:
                const tooltipBorderRadiusLeft =
                    DOMUtil.getBorderRadiusForCorner(this.anchoredComponent._element, position, Alignment.LEFT) / 2;
                const tooltipBorderRadiusRight =
                    DOMUtil.getBorderRadiusForCorner(this.anchoredComponent._element, position, Alignment.RIGHT) / 2;
                const pointerLeft = DOMUtil.convertStyleAttrToNumber(style.left);
                const pointerRightEdge = pointerLeft + pointerWidth;
                const pointerRunoffRight = pointerRightEdge - tooltipHeightWidth.width;
                if (pointerLeft < tooltipBorderRadiusLeft) {
                    style.left = StyleUtil.numberAsPixels(tooltipBorderRadiusLeft)
                }
                if (pointerRunoffRight > 0) {
                    style.left = StyleUtil.numberAsPixels(pointerLeft - pointerRunoffRight - tooltipBorderRadiusRight);
                }
                break;
            case Alignment.LEFT:
            case Alignment.RIGHT:
                const tooltipBorderRadiusTop =
                    DOMUtil.getBorderRadiusForCorner(this.anchoredComponent._element, Alignment.TOP, position) / 2;
                const tooltipBorderRadiusBottom =
                    DOMUtil.getBorderRadiusForCorner(this.anchoredComponent._element, Alignment.BOTTOM, position) / 2;
                const pointerTop = DOMUtil.convertStyleAttrToNumber(style.top);
                const pointerBottomEdge = pointerTop + pointerWidth;
                const pointerRunoffBottom = pointerBottomEdge - tooltipHeightWidth.height;
                if (pointerTop < tooltipBorderRadiusTop) {
                    style.top = StyleUtil.numberAsPixels(tooltipBorderRadiusTop)
                }
                if (pointerRunoffBottom > 0) {
                    style.top = StyleUtil.numberAsPixels(pointerTop - pointerRunoffBottom - tooltipBorderRadiusBottom);
                }
                break;
            default:
                break;
        }
    }

    protected getAdjustedPointerStyle(): Partial<CSSStyleDeclaration> {
        const position = this.currentMutation.newAnchor.position;
        const color = this.determinePointerColor();
        const pointerSize = this.pointerSize;

        switch (position) {
            case Alignment.TOP:
                return {
                    borderLeft: `${pointerSize}px solid transparent`,
                    borderRight: `${pointerSize}px solid transparent`,
                    borderTop: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize * 2}px`,
                    height: `${pointerSize}px`,
                }
            case Alignment.BOTTOM:
                return {
                    borderLeft: `${pointerSize}px solid transparent`,
                    borderRight: `${pointerSize}px solid transparent`,
                    borderBottom: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize * 2}px`,
                    height: `${pointerSize}px`,
                }
            case Alignment.LEFT:
                return {
                    borderTop: `${pointerSize}px solid transparent`,
                    borderBottom: `${pointerSize}px solid transparent`,
                    borderLeft: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize}px`,
                    height: `${pointerSize * 2}px`,
                }
            case Alignment.RIGHT:
                return {
                    borderTop: `${pointerSize}px solid transparent`,
                    borderBottom: `${pointerSize}px solid transparent`,
                    borderRight: `${pointerSize}px solid ${color}`,
                    width: `${pointerSize}px`,
                    height: `${pointerSize * 2}px`,
                }
        }
    }

    protected determinePointerColor(): Color {
        return this.anchoredComponent.backgroundColor;
    }

    getDefaultPosition(): Alignment {
        return Alignment.BOTTOM;
    }

    public static async autoUpdateTooltipPosition(props: AnchoredComponentPositionerProps): Promise<AnchoredComponentPositioner> {
        return this.autoUpdatePosition(props, AnchoredComponentType.TOOLTIP);
    }

    public static async autoUpdateOverlayPosition(props: AnchoredComponentPositionerProps): Promise<AnchoredComponentPositioner>{
        return this.autoUpdatePosition(props, AnchoredComponentType.OVERLAY);
    }

    private static async autoUpdatePosition(props: AnchoredComponentPositionerProps, compType: AnchoredComponentType): Promise<AnchoredComponentPositioner>{
        const positionerClass = DynamicLoader.getClassForPath(positionerClassPath);
        const positioner: AnchoredComponentPositioner = new positionerClass(props, compType);
        await positioner.computeAndUpdatePosition();
        positioner.listenForPositionUpdates();
        return positioner;
    }
}
